import { API_CONFIG, NGROK_HEADERS } from './config'
import { parseValidationErrors, stringifyApiError } from '@/utils/errorHandling'
import type { ValidationError } from '@/utils/errorHandling'

// Generic JSON-serialisable value type (covers objects, arrays, primitives, null)
export type JsonValue = string | number | boolean | null | { [k: string]: JsonValue } | JsonValue[]

interface ApiRequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any
  requiresAuth?: boolean
  signal?: AbortSignal
}

interface ApiResponse<T = unknown> {
  data?: T
  error?: string
  status: number
  statusText: string
  etag?: string
}

/**
 * Result of a binary/file download (e.g. a CSV export). On success `blob` holds the
 * payload and `filename` is parsed from the Content-Disposition header when present.
 * On failure `error` is set and `blob` is undefined.
 */
interface DownloadResult {
  blob?: Blob
  filename?: string
  error?: string
  status: number
  statusText: string
}

/** Pull the filename out of a Content-Disposition header, handling RFC 5987 (filename*=). */
function parseContentDispositionFilename(header: string | null): string | undefined {
  if (!header) return undefined
  const star = /filename\*=(?:UTF-8'')?["']?([^;"']+)/i.exec(header)
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]) } catch { return star[1] }
  }
  const plain = /filename=["']?([^;"']+)/i.exec(header)
  return plain?.[1]
}

// Global error handler type
type ValidationErrorHandler = (errors: ValidationError[]) => void

class ApiClient {
  private baseURL: string
  private getAccessToken?: () => Promise<string | undefined>
  private onLogout?: () => void
  private validationErrorHandler?: ValidationErrorHandler
  private onForbidden?: () => void

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }
  getBaseUrl() { return this.baseURL }

  // Set auth token getter and logout handler
  setAuthHandlers(
    getAccessToken: () => Promise<string | undefined>,
    onLogout: () => void
  ) {
    this.getAccessToken = getAccessToken
    this.onLogout = onLogout
  }

  // Set global validation error handler for 422 responses
  setValidationErrorHandler(handler: ValidationErrorHandler) {
    this.validationErrorHandler = handler
  }

  // Set global forbidden handler for 403 responses (permission changes)
  setForbiddenHandler(handler: () => void) {
    this.onForbidden = handler
  }

  // Request interceptor
  private async prepareRequest(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<[string, RequestInit]> {
    const url = `${this.baseURL}${endpoint}`

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...NGROK_HEADERS,
      ...config.headers,
    }

    // Only set Content-Type to JSON if we're not sending FormData
    if (!(config.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    // Add authorization header if auth is required
    if (config.requiresAuth !== false && this.getAccessToken) {
      try {
        // Add timeout to prevent hanging - Auth0 can sometimes hang indefinitely
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const timeoutPromise = new Promise<undefined>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('getAccessToken timeout after 5s')), 5000)
        })

        let accessToken: string | undefined
        try {
          accessToken = await Promise.race([
            this.getAccessToken(),
            timeoutPromise
          ]) as string | undefined
        } finally {
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId)
          }
        }

        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`
        }
      } catch (error) {
        console.error('Error getting access token:', error)
        // Continue without token - let the server return 401 if needed
      }
    }

    const requestInit: RequestInit = {
      method: config.method || 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    }

    // Add body for non-GET requests
    if (config.body && config.method !== 'GET') {
      if (config.body instanceof FormData) {
        // For FormData, use as-is and let browser set Content-Type with boundary
        requestInit.body = config.body
      } else {
        // For other body types, stringify if needed
        requestInit.body = typeof config.body === 'string'
          ? config.body
          : JSON.stringify(config.body)
      }
    }

    // Attach abort signal if provided
    if (config.signal) {
      requestInit.signal = config.signal
    }

    return [url, requestInit]
  }

  // Response interceptor
  private async processResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const result: ApiResponse<T> = {
      status: response.status,
      statusText: response.statusText,
    }

    // Capture ETag header if present
    const etag = response.headers.get('etag') || response.headers.get('ETag')
    if (etag) {
      result.etag = etag
    }

    // Handle 401 - Auto logout
    if (response.status === 401) {
      if (this.onLogout) {
        this.onLogout()
      }
      result.error = 'Unauthorized - please login again'
      return result
    }

    // Handle 403 - Permission denied (user's roles/capabilities changed)
    if (response.status === 403) {
      if (this.onForbidden) {
        this.onForbidden()
      }
      result.error = 'You do not have permission to perform this action'
      return result
    }

    // Handle 404
    if (response.status === 404) {
      result.error = 'Resource not found'
      return result
    }

    // Handle 304 Not Modified - signal to use cached data.
    //
    // Must come before the !response.ok branch below: `ok` is false for 304, so this check used to
    // sit after that branch and was unreachable, and every 304 came back as
    // `error: "HTTP 304: Not Modified"` (ORB-574).
    //
    // Nothing was observably broken by that, for two independent reasons, and both are worth
    // knowing before trusting this path: useLookups tests `status` before `error` so it coped, and
    // useCache — which tests `error` first and would not have — has no importers. More to the
    // point, no 304 reaches here at all today. `ETag` is not a CORS-safelisted response header and
    // the API does not name it in `Access-Control-Expose-Headers`, so the capture above always
    // reads null cross-origin, `If-None-Match` is never sent, and the server always answers 200.
    //
    // Deliberately leaves `data` unset: that absence is the signal to keep using cached data.
    if (response.status === 304) {
      return result
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type')

      // For validation errors (422) and other JSON error responses
      if (contentType && contentType.includes('application/json')) {
        try {
          const errorJson = await response.json()
          // If the server returns an array (e.g., list of validation errors), serialize it
          if (Array.isArray(errorJson)) {
            result.error = JSON.stringify(errorJson)
            result.status = response.status

            // Global 422 handling - Business Rule Errors
            if (response.status === 422 && this.validationErrorHandler) {
              const validationErrors = parseValidationErrors({ error: result.error })
              if (validationErrors.length > 0) {
                this.validationErrorHandler(validationErrors)
              }
            }

            return result
          }
          // Otherwise attach properties and preserve status
          Object.assign(result, errorJson)
          // ORB-537: that Object.assign copies the body's own `error` field straight onto
          // result.error, and the API sends business-rule failures as a REAL array inside the
          // wrapper — `{ error: [ {Title, Message, FieldName}, … ] }` (ORB-390). The array branch
          // above only catches the pre-ORB-390 shape where the whole body was the array, so the
          // wrapped shape lands a non-string on a field this interface declares as `string`, and
          // every consumer that calls a string method on it throws. errorJson is `any`, so
          // TypeScript cannot see it. Normalize here, at the boundary that owns the contract.
          result.error = stringifyApiError(result.error) || undefined
          // Ensure there's at least an error string for downstream handlers
          if (!result.error) {
            result.error = typeof errorJson.message === 'string'
              ? errorJson.message
              : `HTTP ${response.status}: ${response.statusText}`
          }
          result.status = response.status

          // Global 422 handling - Business Rule Errors
          if (response.status === 422 && this.validationErrorHandler) {
            const validationErrors = parseValidationErrors(result)
            if (validationErrors.length > 0) {
              this.validationErrorHandler(validationErrors)
            }
          }

          return result
        } catch {
          // If JSON parsing fails, fall back to text
          const errorText = await response.text()
          result.error = errorText || `HTTP ${response.status}: ${response.statusText}`
          return result
        }
      } else {
        // Non-JSON error response
        const errorText = await response.text()
        result.error = errorText || `HTTP ${response.status}: ${response.statusText}`
        return result
      }
    }

    // For 204/205 No Content, don't attempt to parse a body
    if (response.status === 204 || response.status === 205) {
      return result
    }

    // Parse successful response
    try {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        result.data = await response.json()
      } else {
        // Non-JSON responses fallback to raw text
        result.data = await response.text() as unknown as T
      }
    } catch (error) {
      console.error('Error parsing response:', error)
      result.error = 'Failed to parse response'
    }

    return result
  }

  // Main request method
  async request<T = unknown>(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<ApiResponse<T>> {
    try {
      const [url, requestInit] = await this.prepareRequest(endpoint, config)
      const response = await fetch(url, requestInit)
      return await this.processResponse<T>(response)
    } catch (error) {
      // Suppress noisy AbortError logs from canceled searches
      if (error && (error as Error).name === 'AbortError') {
        return {
          status: 0,
          statusText: 'Aborted',
          error: 'Request aborted',
        }
      }
      console.error('Network error:', error)

      // A fetch that never got a response (offline, a dropped connection, a CORS refusal) is the
      // same TypeError in every browser, and the page cannot tell which. Calling it a CORS error
      // told users on flaky Wi-Fi the server was misconfigured (ORB-1000), so say the plain thing.
      return {
        status: 0,
        statusText: 'Network Error',
        error: 'Network request failed. Please check your connection.',
      }
    }
  }

  // Convenience methods
  async get<T = unknown>(endpoint: string, config: Omit<ApiRequestConfig, 'method'> = {}) {
    return this.request<T>(endpoint, { ...config, method: 'GET' })
  }

  async post<T = unknown>(endpoint: string, body?: ApiRequestConfig['body'], config: Omit<ApiRequestConfig, 'method' | 'body'> = {}) {
    return this.request<T>(endpoint, { ...config, method: 'POST', body })
  }

  async put<T = unknown>(endpoint: string, body?: ApiRequestConfig['body'], config: Omit<ApiRequestConfig, 'method' | 'body'> = {}) {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body })
  }

  async delete<T = unknown>(endpoint: string, config: Omit<ApiRequestConfig, 'method'> = {}) {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' })
  }

  async patch<T = unknown>(endpoint: string, body?: ApiRequestConfig['body'], config: Omit<ApiRequestConfig, 'method' | 'body'> = {}) {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body })
  }

  /**
   * Fetch a binary/file response (e.g. a CSV export) as a Blob, with auth applied. Mirrors
   * the 401/403 handling of `processResponse` so a download behaves like any other call,
   * but returns the raw bytes plus the server-suggested filename instead of parsed JSON.
   */
  async download(endpoint: string, config: Omit<ApiRequestConfig, 'method'> = {}): Promise<DownloadResult> {
    try {
      const [url, requestInit] = await this.prepareRequest(endpoint, { ...config, method: 'GET' })
      const response = await fetch(url, requestInit)

      if (response.status === 401) {
        this.onLogout?.()
        return { status: 401, statusText: response.statusText, error: 'Unauthorized - please login again' }
      }
      if (response.status === 403) {
        this.onForbidden?.()
        return { status: 403, statusText: response.statusText, error: 'You do not have permission to perform this action' }
      }
      if (!response.ok) {
        const text = await response.text().catch(() => '')
        return { status: response.status, statusText: response.statusText, error: text || `HTTP ${response.status}: ${response.statusText}` }
      }

      const blob = await response.blob()
      const filename = parseContentDispositionFilename(response.headers.get('content-disposition'))
      return { status: response.status, statusText: response.statusText, blob, filename }
    } catch (error) {
      if (error && (error as Error).name === 'AbortError') {
        return { status: 0, statusText: 'Aborted', error: 'Request aborted' }
      }
      console.error('Download error:', error)
      return { status: 0, statusText: 'Network Error', error: 'Network request failed. Please check your connection.' }
    }
  }
}

// Create and export the API client instance
export const apiClient = new ApiClient(API_CONFIG.BASE_URL)

// Export types for use in other files
export type { ApiResponse, ApiRequestConfig, DownloadResult }
